import React, { useState, cloneElement } from 'react';
import { SectionErrorBoundary } from '../components/SectionErrorBoundary';
import { Users, ShoppingBag, DollarSign, BarChart3, Settings, Star, Zap, MessageSquare, Crown, CalendarDays, Layers, TrendingUp, LogOut, FileText, LayoutGrid, Target, Megaphone, Store, LayoutDashboard, Video, Calendar, CreditCard, Menu, X } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { useUser } from '../contexts/UserContext';
import { useLanguage } from '../i18n/LanguageContext';
// Import all tabs (existing and new)
import { OverviewTab } from '../components/astrologer-dashboard/OverviewTab';
import { OrdersTab } from '../components/astrologer-dashboard/OrdersTab';
import { ConsultationsTab } from '../components/astrologer-dashboard/ConsultationsTab';
import { ChatTab } from '../components/astrologer-dashboard/ChatTab';
import { ServicesTab } from '../components/astrologer-dashboard/ServicesTab';
import { MarketplaceTab } from '../components/astrologer-dashboard/MarketplaceTab';
import { CalendarScheduleTab } from '../components/astrologer-dashboard/CalendarScheduleTab';
import { FinancesTab } from '../components/astrologer-dashboard/FinancesTab';
import { PromotionTab } from '../components/astrologer-dashboard/PromotionTab';
import { ReviewsTab } from '../components/astrologer-dashboard/ReviewsTab';
import { StatsTab } from '../components/astrologer-dashboard/StatsTab';
import { ProfileTab } from '../components/astrologer-dashboard/ProfileTab';
import { SettingsSecurityTab } from '../components/astrologer-dashboard/SettingsSecurityTab';
import { SubscriptionTab } from '../components/astrologer-dashboard/SubscriptionTab';
import { AutoServiceDetail } from '../components/astrologer-dashboard/AutoServiceDetail';
interface AstrologerDashboardPageProps {
  onNavigate: (page: string) => void;
}
export function AstrologerDashboardPage({
  onNavigate
}: AstrologerDashboardPageProps) {
  const {
    user,
    logout
  } = useUser();
  const {
    t
  } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const [activeAutoService, setActiveAutoService] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };
  // 16-section sidebar grouped logically
  const menuGroups = [{
    title: t('astrodash.group.main'),
    items: [{
      id: 'overview',
      label: t('astrodash.tab.overview'),
      icon: <LayoutDashboard className="w-5 h-5" data-id="element-20170" />
    }, {
      id: 'orders',
      label: t('astrodash.tab.orders'),
      icon: <ShoppingBag className="w-5 h-5" data-id="element-20171" />
    }, {
      id: 'consultations',
      label: t('astrodash.tab.consultations'),
      icon: <Video className="w-5 h-5" data-id="element-20172" />
    }, {
      id: 'chat',
      label: t('astrodash.tab.chat'),
      icon: <MessageSquare className="w-5 h-5" data-id="element-20173" />
    }]
  }, {
    title: t('astrodash.group.products'),
    items: [{
      id: 'services',
      label: t('astrodash.tab.services'),
      icon: <Layers className="w-5 h-5" data-id="element-20174" />
    }, {
      id: 'marketplace',
      label: t('astrodash.tab.marketplace'),
      icon: <Store className="w-5 h-5" data-id="element-20175" />
    }, {
      id: 'calendar',
      label: t('astrodash.tab.calendar'),
      icon: <Calendar className="w-5 h-5" data-id="element-20176" />
    }]
  }, {
    title: t('astrodash.group.business'),
    items: [{
      id: 'finances',
      label: t('astrodash.tab.finances'),
      icon: <DollarSign className="w-5 h-5" data-id="element-20177" />
    }, {
      id: 'promotion',
      label: t('astrodash.tab.promotion'),
      icon: <Zap className="w-5 h-5" data-id="element-20178" />
    }, {
      id: 'reviews',
      label: t('astrodash.tab.reviews'),
      icon: <Star className="w-5 h-5" data-id="element-20179" />
    }, {
      id: 'stats',
      label: t('astrodash.tab.stats'),
      icon: <BarChart3 className="w-5 h-5" data-id="element-20180" />
    }]
  }, {
    title: t('astrodash.group.settings'),
    items: [{
      id: 'profile',
      label: t('astrodash.tab.profile'),
      icon: <Users className="w-5 h-5" data-id="element-20181" />
    }, {
      id: 'settings',
      label: t('astrodash.tab.settings'),
      icon: <Settings className="w-5 h-5" data-id="element-20182" />
    }, {
      id: 'subscription',
      label: t('astrodash.tab.subscription'),
      icon: <CreditCard className="w-5 h-5" data-id="element-20183" />
    }]
  }];
  const handleTabChange = (id: string) => {
    setActiveTab(id);
    setActiveAutoService(null);
    setMobileMenuOpen(false);
  };
  const renderContent = () => {
    if (activeAutoService) {
      return <AutoServiceDetail activeAutoService={activeAutoService} setActiveAutoService={setActiveAutoService} showToast={showToast} data-id="element-20184" />;
    }
    switch (activeTab) {
      case 'overview':
        return <OverviewTab showToast={showToast} setActiveTab={setActiveTab} data-id="element-20185" />;
      case 'orders':
        return <OrdersTab showToast={showToast} setActiveTab={setActiveTab} data-id="element-20186" />;
      case 'consultations':
        return <ConsultationsTab showToast={showToast} setActiveTab={setActiveTab} data-id="element-20187" />;
      case 'chat':
        return <ChatTab showToast={showToast} setActiveTab={setActiveTab} data-id="element-20188" />;
      case 'services':
        return <ServicesTab showToast={showToast} setActiveTab={setActiveTab} data-id="element-20189" />;
      case 'marketplace':
        return <MarketplaceTab showToast={showToast} setActiveTab={setActiveTab} data-id="element-20190" />;
      case 'calendar':
        return <CalendarScheduleTab showToast={showToast} data-id="element-20191" />;
      case 'finances':
        return <FinancesTab showToast={showToast} setActiveTab={setActiveTab} data-id="element-20192" />;
      case 'promotion':
        return <PromotionTab showToast={showToast} setActiveAutoService={setActiveAutoService} setActiveTab={setActiveTab} data-id="element-20193" />;
      case 'reviews':
        return <ReviewsTab showToast={showToast} setActiveTab={setActiveTab} data-id="element-20194" />;
      case 'stats':
        return <StatsTab showToast={showToast} setActiveTab={setActiveTab} data-id="element-20195" />;
      case 'profile':
        return <ProfileTab showToast={showToast} onNavigate={onNavigate} setActiveTab={handleTabChange} data-id="element-20196" />;
      case 'settings':
        return <SettingsSecurityTab showToast={showToast} initialTab="general" data-id="element-20197" />;
      case 'subscription':
        return <SubscriptionTab showToast={showToast} data-id="element-20198" />;
      default:
        return <div className="flex flex-col items-center justify-center py-20 text-slate-500" data-id="element-20199">
            <Settings className="w-12 h-12 text-slate-300 mb-3" data-id="element-20200" />
            <p className="text-lg font-medium text-slate-900 mb-1" data-id="element-20201">
              {t('astrodash.common.noData')}
            </p>
            <p className="text-sm mb-4" data-id="element-20202">{t('astrodash.tab.overview')}</p>
            <Button variant="primary" size="sm" onClick={() => setActiveTab('overview')} data-id="element-20203">
              {t('astrodash.common.back')}
            </Button>
          </div>;
    }
  };
  return <div className="w-full bg-slate-50 min-h-screen pb-24" data-id="element-20204">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 lg:pt-12" data-id="element-20205">
        {/* Mobile Header & Menu Toggle */}
        <div className="lg:hidden flex items-center justify-between bg-white p-4 rounded-2xl border border-slate-200 shadow-sm mb-6" data-id="element-20206">
          <div className="flex items-center gap-3" data-id="element-20207">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center text-sm font-bold text-indigo-700 uppercase" data-id="element-20208">
              {user?.name?.slice(0, 2) || 'АС'}
            </div>
            <div data-id="element-20209">
              <div className="font-bold text-slate-900 text-sm" data-id="element-20210">
                {user?.name || t('astrodash.role')}
              </div>
              <Badge variant="pro" size="sm" className="mt-0.5" data-id="element-20211">
                <Crown className="w-3 h-3 mr-1" data-id="element-20212" /> PRO
              </Badge>
            </div>
          </div>
          <Button variant="ghost" size="sm" className="p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-id="element-20213">
            {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-20214" /> : <Menu className="w-6 h-6" data-id="element-20215" />}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-20216">
          {/* Sidebar (Desktop & Mobile Dropdown) */}
          <div className={`lg:col-span-3 ${mobileMenuOpen ? 'block' : 'hidden lg:block'}`} data-id="element-20217">
            <Card padding="none" className="sticky top-24 overflow-hidden border-slate-200 shadow-sm mb-6 lg:mb-0" data-id="element-20218">
              {/* Desktop User Info */}
              <div className="hidden lg:block p-6 border-b border-slate-100 bg-slate-50/50" data-id="element-20219">
                <div className="flex flex-col items-center text-center" data-id="element-20220">
                  <div className="w-16 h-16 rounded-2xl bg-indigo-100 flex items-center justify-center text-2xl font-bold text-indigo-700 mb-3 uppercase" data-id="element-20221">
                    {user?.name?.slice(0, 2) || 'АС'}
                  </div>
                  <div className="font-bold text-slate-900 text-lg mb-2" data-id="element-20222">
                    {user?.name || t('astrodash.role')}
                  </div>
                  <div className="w-full bg-indigo-50 border border-indigo-100 rounded-xl p-3 flex flex-col items-center gap-2" data-id="element-20223">
                    <div className="flex items-center text-indigo-700 font-medium text-sm" data-id="element-20224">
                      <Crown className="w-4 h-4 mr-1.5" data-id="element-20225" /> PRO{' '}
                      {t('astrodash.promo.tariff')}
                    </div>
                    <Button variant="outline" size="sm" fullWidth className="text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-100/50 bg-white" onClick={() => handleTabChange('subscription')} data-id="element-20226">
                      {t('astrodash.tab.subscription')}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Navigation Menu */}
              <nav className="p-3 max-h-[60vh] lg:h-[calc(100vh-300px)] overflow-y-auto custom-scrollbar" data-id="element-20227">
                {menuGroups.map((group, idx) => <div key={idx} className="mb-4 last:mb-0" data-id="element-20228">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 px-3" data-id="element-20229">
                      {group.title}
                    </div>
                    {group.items.map(item => <button key={item.id} onClick={() => handleTabChange(item.id)} title={item.label} className={`w-full flex items-center px-3 py-2 mb-0.5 rounded-lg text-sm font-medium transition-colors ${activeTab === item.id && !activeAutoService ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-20230">
                        <span className={`mr-3 shrink-0 ${activeTab === item.id && !activeAutoService ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-20231">
                          {item.icon}
                        </span>
                        <span className="truncate" data-id="element-20232">{item.label}</span>
                      </button>)}
                  </div>)}

                <div className="pt-4 mt-2 border-t border-slate-100" data-id="element-20233">
                  <button onClick={() => {
                  logout();
                  onNavigate('home');
                }} className="w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium text-rose-600 hover:bg-rose-50 transition-colors" data-id="element-20234">
                    <LogOut className="w-4 h-4 mr-3 text-rose-400" data-id="element-20235" />{' '}
                    {t('astrodash.logout')}
                  </button>
                </div>
              </nav>
            </Card>
          </div>

          {/* Main Content Area */}
          <div className={`lg:col-span-9 ${mobileMenuOpen ? 'hidden lg:block' : 'block'}`} data-id="element-20236">
            <SectionErrorBoundary key={activeTab} sectionName={`astrologer-${activeTab}`} fallbackMinHeight="min-h-[400px]" data-id="element-20237">
              {renderContent()}
            </SectionErrorBoundary>
          </div>
        </div>
      </div>

      {/* Toast notification */}
      {toast && <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-800 text-white text-sm font-medium px-4 py-2.5 rounded-xl shadow-lg animate-[fadeIn_0.2s_ease-out]" data-id="element-20238">
          {toast}
        </div>}
    </div>;
}